import { useEffect, useState, type ReactNode } from "react";
import { tEnglish as t, type PhraseKey } from "@buza/i18n";

// The parlour app layout, following the console shell (S-062, BD-61): a header, navigation
// between screens chosen by the location hash (#/profile), and the screen. The header carries the
// signed-in user and sign-out (S-007, passed in as `header`); S02 (home), S04, S05 and the Lite,
// CRM and remembrance screens add their routes here.

export interface ScreenDef {
  id: string;
  label: PhraseKey;
  render: () => ReactNode;
}

export function screenFromHash(hash: string, screens: readonly ScreenDef[]): string {
  const id = hash.replace(/^#\/?/, "").split(/[/?]/)[0];
  return screens.find((s) => s.id === id)?.id ?? screens[0]!.id;
}

export function ParlourShell({ screens, header }: { screens: readonly ScreenDef[]; header?: ReactNode }) {
  const [current, setCurrent] = useState(() => screenFromHash(window.location.hash, screens));

  useEffect(() => {
    const onHash = () => setCurrent(screenFromHash(window.location.hash, screens));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [screens]);

  const screen = screens.find((s) => s.id === current)!;
  return (
    <div className="parlour">
      <header>
        <p className="app-name">{t("parlour.shell.app_name")}</p>
        {header}
      </header>
      <nav aria-label={t("parlour.profile.nav_label")}>
        <ul>
          {screens.map((s) => (
            <li key={s.id}>
              <a href={`#/${s.id}`} aria-current={s.id === current ? "page" : undefined} onClick={() => setCurrent(s.id)}>
                {t(s.label)}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      <main>{screen.render()}</main>
    </div>
  );
}
